import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Title2,
  Title3,
  Body1,
  Body1Strong,
  Button,
  Badge,
  Caption1,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular,
  ArrowUpload24Regular,
  Video24Regular,
  Edit24Regular,
} from '@fluentui/react-icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useCaseStore, CaseType, CaseStatus, CasePriority, VideoStatus } from '../stores/caseStore'
import { VideoUpload } from '../components/VideoUpload'
import { EditCaseDialog } from '../components/EditCaseDialog'

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  content: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1200px',
    margin: '0 auto',
  },
  caseHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalXXL,
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  caseTitle: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  section: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  uploadSection: {
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    marginBottom: tokens.spacingVerticalL,
  },
  tableContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'hidden',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  emptyIcon: {
    fontSize: '64px',
    color: tokens.colorNeutralForeground3,
  },
  caseFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: tokens.spacingVerticalM,
    marginTop: tokens.spacingVerticalM,
  },
  caseField: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
})

const getCaseTypeBadgeColor = (type: CaseType): 'informative' | 'warning' | 'danger' | 'success' => {
  switch (type) {
    case 'investigation':
      return 'informative'
    case 'surveillance':
      return 'warning'
    case 'incident':
      return 'danger'
    default:
      return 'success'
  }
}

const getCaseStatusBadgeColor = (status: CaseStatus): 'success' | 'subtle' | 'informative' => {
  switch (status) {
    case 'active':
      return 'success'
    case 'closed':
      return 'subtle'
    default:
      return 'informative'
  }
}

const getCasePriorityBadgeColor = (priority: CasePriority): 'subtle' | 'informative' | 'warning' | 'danger' => {
  switch (priority) {
    case 'low':
      return 'subtle'
    case 'medium':
      return 'informative'
    case 'high':
      return 'warning'
    case 'critical':
      return 'danger'
  }
}

const getVideoStatusBadgeColor = (status: VideoStatus): 'informative' | 'warning' | 'success' | 'danger' | 'subtle' => {
  switch (status) {
    case 'pending':
      return 'subtle'
    case 'uploading':
      return 'informative'
    case 'processing':
      return 'warning'
    case 'ready':
      return 'success'
    case 'failed':
      return 'danger'
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function CaseDetail() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const cases = useCaseStore((s) => s.cases)
  const getVideosForCase = useCaseStore((s) => s.getVideosForCase)
  
  const [showUpload, setShowUpload] = useState(false)
  const [showEdit, setShowEdit] = useState(false)

  const caseItem = cases.find((c) => c.id === id)
  const videos = id ? getVideosForCase(id) : []

  if (!caseItem) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <Button
              icon={<ArrowLeft24Regular />}
              appearance="subtle"
              onClick={() => navigate('/cases')}
              aria-label="Back to cases"
            />
            <Title2>Case Not Found</Title2>
          </div>
        </header>
        <main className={styles.content}>
          <Body1>The requested case could not be found.</Body1>
        </main>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeft24Regular />}
            appearance="subtle"
            onClick={() => navigate('/cases')}
            aria-label="Back to cases"
          />
          <Title2>Case Details</Title2>
        </div>
        <Button
          icon={<Edit24Regular />}
          appearance="secondary"
          onClick={() => setShowEdit(true)}
        >
          Edit Case
        </Button>
      </header>

      <main className={styles.content}>
        <div className={styles.caseHeader}>
          <div className={styles.caseTitle}>
            <Caption1>{caseItem.caseNumber}</Caption1>
            <Title2>{caseItem.name}</Title2>
          </div>
          
          <div className={styles.badges}>
            <Badge appearance="filled" color={getCaseTypeBadgeColor(caseItem.type)}>
              {caseItem.type}
            </Badge>
            <Badge appearance="filled" color={getCaseStatusBadgeColor(caseItem.status)}>
              {caseItem.status}
            </Badge>
            <Badge appearance="filled" color={getCasePriorityBadgeColor(caseItem.priority)}>
              {caseItem.priority} priority
            </Badge>
          </div>
          
          {caseItem.description && <Body1>{caseItem.description}</Body1>}

          <div className={styles.caseFields}>
            {caseItem.incidentDate && (
              <div className={styles.caseField}>
                <Caption1>Incident Date</Caption1>
                <Body1Strong>{new Date(caseItem.incidentDate + 'T00:00:00Z').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}</Body1Strong>
              </div>
            )}
            {caseItem.location && (
              <div className={styles.caseField}>
                <Caption1>Location</Caption1>
                <Body1Strong>{caseItem.location}</Body1Strong>
              </div>
            )}
            {caseItem.assignedTo && (
              <div className={styles.caseField}>
                <Caption1>Assigned To</Caption1>
                <Body1Strong>{caseItem.assignedTo}</Body1Strong>
              </div>
            )}
            <div className={styles.caseField}>
              <Caption1>Created</Caption1>
              <Body1Strong>{formatDate(caseItem.createdAt)}</Body1Strong>
            </div>
            <div className={styles.caseField}>
              <Caption1>Last Updated</Caption1>
              <Body1Strong>{formatDate(caseItem.updatedAt)}</Body1Strong>
            </div>
          </div>

          {caseItem.notes && (
            <div className={styles.caseField}>
              <Caption1>Notes</Caption1>
              <Body1>{caseItem.notes}</Body1>
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Title3>Videos</Title3>
            {!showUpload && (
              <Button
                icon={<ArrowUpload24Regular />}
                appearance="primary"
                onClick={() => setShowUpload(true)}
              >
                Upload Video
              </Button>
            )}
          </div>

          {showUpload && (
            <div className={styles.uploadSection}>
              <VideoUpload
                caseId={caseItem.id}
                onUploaded={() => setShowUpload(false)}
              />
            </div>
          )}

          {videos.length === 0 ? (
            <div className={styles.emptyState}>
              <Video24Regular className={styles.emptyIcon} />
              <div>
                <Title3>No videos uploaded yet</Title3>
                <Body1>Upload your first video to get started</Body1>
              </div>
              {!showUpload && (
                <Button
                  appearance="primary"
                  onClick={() => setShowUpload(true)}
                >
                  Upload First Video
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>File Name</TableHeaderCell>
                    <TableHeaderCell>Size</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell>Uploaded</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {videos.map((video) => (
                    <TableRow key={video.id}>
                      <TableCell>{video.fileName}</TableCell>
                      <TableCell>{formatFileSize(video.fileSize)}</TableCell>
                      <TableCell>
                        <Badge
                          appearance="filled"
                          color={getVideoStatusBadgeColor(video.status)}
                        >
                          {video.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(video.uploadedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <EditCaseDialog
        open={showEdit}
        onClose={() => setShowEdit(false)}
        caseItem={caseItem}
      />
    </div>
  )
}
